import { formatNir } from './nir';

export const PATIENT_DATA_COLLECTION_KEYS = [
  'PHONE',
  'EMAIL',
  'ADDRESS',
  'PROFESSION',
  'BIRTH_NAME',
  'BIRTH_PLACE',
  'SOCIAL_SECURITY_NUMBER',
] as const;

export type PatientDataCollectionKey =
  (typeof PATIENT_DATA_COLLECTION_KEYS)[number];

export type PatientDataCollectionFieldConfig = {
  enabled: boolean;
  required: boolean;
};

export type PatientDataCollectionConfig = Record<
  PatientDataCollectionKey,
  PatientDataCollectionFieldConfig
>;

export type PatientDataCollectionGroup =
  | 'IDENTITY'
  | 'CONTACT'
  | 'MEDICO_SOCIAL'
  | 'ADMINISTRATIVE';

export const PATIENT_DATA_COLLECTION_FIELDS: Record<
  PatientDataCollectionKey,
  { label: string; group: PatientDataCollectionGroup }
> = {
  BIRTH_NAME: { label: 'Nom de naissance', group: 'IDENTITY' },
  BIRTH_PLACE: { label: 'Commune de naissance', group: 'IDENTITY' },
  PHONE: { label: 'Téléphone', group: 'CONTACT' },
  EMAIL: { label: 'Email', group: 'CONTACT' },
  ADDRESS: { label: 'Adresse postale', group: 'CONTACT' },
  PROFESSION: { label: 'Profession', group: 'MEDICO_SOCIAL' },
  SOCIAL_SECURITY_NUMBER: {
    label: 'N° de sécurité sociale',
    group: 'ADMINISTRATIVE',
  },
};

export const PATIENT_DATA_COLLECTION_GROUP_LABELS: Record<
  PatientDataCollectionGroup,
  string
> = {
  IDENTITY: 'Identité',
  CONTACT: 'Coordonnées',
  MEDICO_SOCIAL: 'Médico-social',
  ADMINISTRATIVE: 'Administratif',
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

/**
 * Normalizes the raw `Practice.patientDataCollectionConfig` JSON. A missing
 * config or key means the field is not collected (current behavior), unknown
 * keys are dropped, and a field can only be required when it is enabled.
 */
export const resolvePatientDataCollectionConfig = (
  raw: unknown,
  { keepRequiredWhenDisabled = false } = {},
): PatientDataCollectionConfig => {
  const source = isRecord(raw) ? raw : {};

  return PATIENT_DATA_COLLECTION_KEYS.reduce((config, key) => {
    const rawField = source[key];
    const field = isRecord(rawField) ? rawField : {};
    const enabled = field.enabled === true;
    const required = field.required === true;
    config[key] = {
      enabled,
      required: (enabled || keepRequiredWhenDisabled) && required,
    };
    return config;
  }, {} as PatientDataCollectionConfig);
};

export const getBornLabel = (sex?: string | null): string => {
  if (sex === 'FEMALE') return 'Née';
  if (sex === 'MALE') return 'Né';
  return 'Né(e)';
};

/** "Née Martin" / "Né Martin" / "Né(e) Martin" from the patient's sex. */
export const getBirthNameLabel = (
  birthName: string,
  sex?: string | null,
): string => `${getBornLabel(sex)} ${birthName}`;

export const getBirthPlaceLabel = (
  birthPlace: string,
  sex?: string | null,
): string => `${getBornLabel(sex)} à ${birthPlace}`;

const FRENCH_POSTAL_CODE_REGEX = /^\d{5}$/;

export const isFrenchPostalCode = (postalCode: string): boolean =>
  FRENCH_POSTAL_CODE_REGEX.test(postalCode);

export type PatientAddressFields = {
  addressStreet?: string | null;
  addressPostalCode?: string | null;
  addressCity?: string | null;
};

export const formatPatientAddress = (
  address?: PatientAddressFields | null,
): string | undefined => {
  const cityLine = [address?.addressPostalCode, address?.addressCity]
    .filter(Boolean)
    .join(' ');
  return (
    [address?.addressStreet, cityLine].filter(Boolean).join(', ') || undefined
  );
};

/** Patient columns filled by each collected field. */
export const PATIENT_DATA_COLLECTION_COLUMNS = {
  PHONE: ['phone'],
  EMAIL: ['email'],
  ADDRESS: [
    'addressStreet',
    'addressPostalCode',
    'addressCity',
    'addressCountry',
  ],
  // Collected here, but shown through the KB #PROFESSION question (report
  // context section), not as a native patient field.
  PROFESSION: [],
  BIRTH_NAME: ['birthName'],
  BIRTH_PLACE: ['birthPlace', 'birthPlaceInseeCode'],
  SOCIAL_SECURITY_NUMBER: ['socialSecurityNumber'],
} as const satisfies Record<PatientDataCollectionKey, readonly string[]>;

export type CollectedPatientColumn =
  (typeof PATIENT_DATA_COLLECTION_COLUMNS)[PatientDataCollectionKey][number];

export type CollectedPatientFields = Partial<
  Record<CollectedPatientColumn, string | null>
>;

export const filterCollectedPatientFields = <T extends CollectedPatientFields>(
  patient: T,
  rawConfig: unknown,
): T => {
  const config = resolvePatientDataCollectionConfig(rawConfig);
  const filtered = { ...patient };
  for (const key of PATIENT_DATA_COLLECTION_KEYS) {
    if (config[key].enabled) continue;
    for (const column of PATIENT_DATA_COLLECTION_COLUMNS[key]) {
      if (column in filtered)
        (filtered as CollectedPatientFields)[column] = null;
    }
  }
  return filtered;
};

// Patient columns a required field needs filled in to count as answered.
const REQUIRED_PATIENT_COLUMNS = {
  PHONE: ['phone'],
  EMAIL: ['email'],
  ADDRESS: ['addressStreet', 'addressPostalCode', 'addressCity'],
  PROFESSION: ['profession'],
  BIRTH_NAME: ['birthName'],
  BIRTH_PLACE: ['birthPlace'],
  SOCIAL_SECURITY_NUMBER: ['socialSecurityNumber'],
} as const satisfies Record<PatientDataCollectionKey, readonly string[]>;

export type RequiredPatientFields = Partial<
  Record<
    (typeof REQUIRED_PATIENT_COLUMNS)[PatientDataCollectionKey][number],
    string | null
  >
>;

/** The fields the practice requires that the patient has not filled in. */
export const getMissingRequiredPatientFields = (
  patient: RequiredPatientFields,
  config: PatientDataCollectionConfig,
): PatientDataCollectionKey[] =>
  PATIENT_DATA_COLLECTION_KEYS.filter(
    (key) =>
      config[key].required &&
      REQUIRED_PATIENT_COLUMNS[key].some((column) => !patient[column]?.trim()),
  );

export type PatientDetail = { label: string; value: string };

export const getPatientDetails = (
  patient?: CollectedPatientFields | null,
): PatientDetail[] =>
  [
    { label: 'Adresse', value: formatPatientAddress(patient) },
    { label: 'Email', value: patient?.email ?? undefined },
    {
      label: 'N° de sécurité sociale',
      value: patient?.socialSecurityNumber
        ? formatNir(patient.socialSecurityNumber)
        : undefined,
    },
  ].filter((detail): detail is PatientDetail => Boolean(detail.value));
